/**
 * useFeatureVisibility — client-side mirror of the backend visibility evaluator.
 *
 * Reads the current session (user role + isNeeveEmployee + org overrides) plus
 * the per-user "preview as consumer" toggle (localStorage), and returns:
 *   - isVisible(featureId): boolean
 *   - previewAsConsumer: boolean
 *   - setPreviewAsConsumer(value): void
 *
 * The backend remains the security boundary — this hook only drives UI gating
 * (sidebar items, route redirects, ChatPanel). Any API endpoint that surfaces
 * gated data must be wrapped with `requireFeature()` on the server.
 *
 * The `x-preview-as-consumer` header is written by the API client on every
 * request when the toggle is on; see `frontend/src/services/api.ts`.
 *
 * Keep this evaluator in sync with backend/src/services/feature-visibility.service.ts.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './useAuth';
import { getFeature } from '@/config/features';

/**
 * Preview stage — replaces the previous binary previewAsConsumer toggle.
 * Lets devs preview the UI as if they were a user in a specific tier:
 *   - 'full' (default): @neeve.ai employee view — see everything
 *   - 'deep':           paying Deep Insights tenant — sees mvp + base + deep
 *   - 'base':           Base-unlocked tenant       — sees mvp + base
 *   - 'mvp':            MVP-only consumer (default for non-employees) — sees only mvp
 */
export type PreviewStage = 'full' | 'deep' | 'base' | 'mvp';

const PREVIEW_STORAGE_KEY = 'insights.previewStage';
const LEGACY_TOGGLE_KEY = 'insights.previewAsConsumer'; // pre-stage-chips key — migrated on read

function readPreviewStage(): PreviewStage {
  if (typeof window === 'undefined') return 'full';
  const raw = window.localStorage.getItem(PREVIEW_STORAGE_KEY);
  if (raw === 'full' || raw === 'deep' || raw === 'base' || raw === 'mvp') return raw;
  // Migration: if a user previously had the binary previewAsConsumer toggle on,
  // map it to 'mvp' (most restrictive preview) which matches the old behavior.
  if (window.localStorage.getItem(LEGACY_TOGGLE_KEY) === '1') return 'mvp';
  return 'full';
}

export function useFeatureVisibility() {
  const { session } = useAuth();
  const [previewStage, setPreviewStageState] = useState<PreviewStage>(readPreviewStage);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === PREVIEW_STORAGE_KEY) setPreviewStageState(readPreviewStage());
    };
    const onCustom = () => setPreviewStageState(readPreviewStage());
    window.addEventListener('storage', onStorage);
    window.addEventListener('insights:previewStageChanged', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('insights:previewStageChanged', onCustom);
    };
  }, []);

  const setPreviewStage = useCallback((stage: PreviewStage) => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(PREVIEW_STORAGE_KEY, stage);
    // Clear the legacy key so subsequent readers don't get confused.
    window.localStorage.removeItem(LEGACY_TOGGLE_KEY);
    window.dispatchEvent(new Event('insights:previewStageChanged'));
    setPreviewStageState(stage);
  }, []);

  // Backwards-compat alias — true when previewing as the most restrictive consumer.
  const previewAsConsumer = previewStage === 'mvp';
  const setPreviewAsConsumer = useCallback(
    (on: boolean) => setPreviewStage(on ? 'mvp' : 'full'),
    [setPreviewStage]
  );

  const isNeeveEmployee = session?.isNeeveEmployee === true;
  const role = session?.role;

  // Memoize Sets so they only change when the underlying session overrides change.
  // Without useMemo, new Set() runs every render → isVisible's useCallback would
  // bust on every render too, defeating downstream memoization.
  const overridesEnabled = useMemo(
    () => new Set(session?.featureOverrides?.enabled ?? []),
    [session?.featureOverrides?.enabled]
  );
  const overridesDisabled = useMemo(
    () => new Set(session?.featureOverrides?.disabled ?? []),
    [session?.featureOverrides?.disabled]
  );

  const isVisible = useCallback(
    (featureId: string): boolean => {
      const feature = getFeature(featureId);
      if (!feature) return false;

      // Dev Console scope is override-immune for devs.
      if (feature.scope === 'dev-console' && role === 'dev') {
        return true;
      }

      if (overridesDisabled.has(featureId)) return false;
      if (overridesEnabled.has(featureId)) return true;

      if (feature.stage === 'mvp') return true;

      // For base/deep stages, an @neeve.ai employee can preview as a specific tier.
      // Non-employees: only their actual org overrides count (handled above).
      if (isNeeveEmployee) {
        if (previewStage === 'full') return true;
        if (feature.stage === 'base' && (previewStage === 'base' || previewStage === 'deep')) return true;
        if (feature.stage === 'deep' && previewStage === 'deep') return true;
      }

      return false;
    },
    [role, isNeeveEmployee, previewStage, overridesEnabled, overridesDisabled]
  );

  return {
    isVisible,
    /** New 4-state preview control. */
    previewStage,
    setPreviewStage,
    /** Backwards-compat — true when previewStage === 'mvp'. */
    previewAsConsumer,
    setPreviewAsConsumer,
    isNeeveEmployee,
    role,
  };
}
